import React, { useState } from 'react';
import { useField } from 'formik';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    borderLeft: '4px solid #000',
    paddingLeft: 12,
    paddingTop: 4,
    marginBottom: 1,
    paddingBottom: 6,

    '&.focused': {
      borderLeftColor: theme.palette.primary.main,
    },

    '&.error': {
      borderLeftColor: theme.palette.error.main,
    },

    '&.disabled': {
      borderLeftColor: '#ccc',
    },
        // 🔽 Compact / dense mode
    '&.dense': {
      paddingTop: 6,
      paddingBottom: 4,
    },
  },

  /** No left accent — used on onboarding branding step */
  flat: {
    position: 'relative',
    borderLeft: 'none',
    paddingLeft: 0,
    paddingTop: 4,
    marginBottom: 1,
    paddingBottom: 6,
    '&.dense': {
      paddingTop: 6,
      paddingBottom: 4,
    },
  },

  dashboard: {
    position: 'relative',
    paddingTop: 4,
    marginBottom: 1,
    paddingBottom: 6,
  },

  textField: {
    margin: 0,
  },

  inputRoot: {
    padding: 0,
  },

  inputRootDashboard: {
    padding: '0 12px',
    minHeight: 'var(--app-control-height, 44px)',
    borderRadius: 12,
    background: 'var(--surface-subtle, #f3f3f6)',
    alignItems: 'center',
    transition: 'background-color 0.15s ease',
    color: 'var(--text-primary, #0f172a)',

    '&:hover': {
      background: 'var(--surface-subtle-hover, #e8e8ee)',
    },

    '&.Mui-focused': {
      background: 'var(--surface-subtle-hover, #e8e8ee)',
    },

    '&.Mui-disabled': {
      background: 'var(--surface-subtle, #f3f3f6)',
      opacity: 0.7,
    },

    '& input': {
      color: 'var(--text-primary, #0f172a) !important',
      WebkitTextFillColor: 'var(--text-primary, #0f172a)',
      caretColor: 'var(--text-primary, #0f172a)',
      opacity: '1 !important',
    },

    '& input::placeholder': {
      color: 'var(--text-muted, #94a3b8)',
      opacity: 1,
      WebkitTextFillColor: 'var(--text-muted, #94a3b8)',
    },

    '& input:-webkit-autofill': {
      WebkitTextFillColor: 'var(--text-primary, #0f172a)',
      caretColor: 'var(--text-primary, #0f172a)',
      transition: 'background-color 9999s ease-out 0s',
    },

    '& .MuiInputBase-input.Mui-disabled': {
      color: 'var(--text-secondary-strong, #475569) !important',
      WebkitTextFillColor: 'var(--text-secondary-strong, #475569)',
      opacity: '1 !important',
    },
  },

  input: {
    padding: '4px 0',
    fontSize: 14,
    lineHeight: 1.4,
  },

  inputDashboard: {
    padding: '0 !important',
    fontSize: '0.875rem',
    lineHeight: 1.45,
    color: 'var(--text-primary, #0f172a)',
    WebkitTextFillColor: 'var(--text-primary, #0f172a)',
    caretColor: 'var(--text-primary, #0f172a)',
  },

  label: {
    fontSize: 12,
    transformOrigin: 'top left',
  },

  labelDense: {
    fontSize: 11,
  },

  helperText: {
    margin: '2px 0 0 0',
    fontSize: 12,
  },
}));

type Props = TextFieldProps & {
  name: string;
  dense?: boolean;
  flat?: boolean;
  dashboardChrome?: boolean;
};

const LeftBorderInputFormik: React.FC<Props> = ({
  dense = false,
  flat = false,
  dashboardChrome = false,
  type,
  InputProps: inputPropsProp,
  InputLabelProps: inputLabelPropsProp,
  ...props
}) => {
  const classes = useStyles();
  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [field, meta] = useField(props.name);
  const showError = Boolean(meta.touched && meta.error);
  const isPassword = type === 'password';
  const resolvedType = isPassword && showPassword ? 'text' : type;

  const passwordAdornment = isPassword ? (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onClick={() => setShowPassword((v) => !v)}
        edge="end"
        size="small"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  ) : undefined;

  return (
    <div
      className={clsx(dashboardChrome ? classes.dashboard : flat ? classes.flat : classes.container, {
        focused,
        error: showError,
        disabled: props.disabled,
        dense,
      })}
    >
      <TextField
        {...props}
        {...field}
        type={resolvedType}
        fullWidth
        variant="standard"
        error={showError}
        helperText={showError ? meta.error : undefined}
        className={classes.textField}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          field.onBlur(e);
        }}
        InputProps={{
          ...inputPropsProp,
          disableUnderline: true,
          classes: {
            ...inputPropsProp?.classes,
            root: clsx(dashboardChrome ? classes.inputRootDashboard : classes.inputRoot, inputPropsProp?.classes?.root),
            input: clsx(classes.input, dashboardChrome && classes.inputDashboard, inputPropsProp?.classes?.input),
          },
          endAdornment: isPassword ? (
            <>
              {inputPropsProp?.endAdornment}
              {passwordAdornment}
            </>
          ) : inputPropsProp?.endAdornment,
        }}
        InputLabelProps={{
          // Standard variant + filled dashboard input: without shrink, MUI keeps the label on the
          // baseline and suppresses the placeholder; the grey box can hide the label when empty.
          ...(dashboardChrome ? { shrink: true } : {}),
          ...inputLabelPropsProp,
          classes: {
            root: classes.label,
            ...inputLabelPropsProp?.classes,
          },
        }}
        FormHelperTextProps={{ className: classes.helperText }}
      />
    </div>
  );
};

export default LeftBorderInputFormik;
